import React, { useCallback, useEffect, useMemo, useState } from 'react';

import MovementsTable from './MovementsTable';

import Button from 'CommonComponents/Button';
import DateRangePickerField from 'CommonComponents/DateRangePickerField';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';

import FileExportIcon from 'Assets/icons/file-export.svg';

const RANGE_BUDGET = 'budget';
const RANGE_ALL = 'all';
const RANGE_CUSTOM = 'custom';

export default function MovementsTab(props) {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 50 });
    // Undefined across every contingency, which has no one balance; null for a deactivated one.
    const [closingBalance, setClosingBalance] = useState(undefined);
    const [contingency, setContingency] = useState('all');
    const [type, setType] = useState('all');
    const [range, setRange] = useState(RANGE_BUDGET);
    const [dates, setDates] = useState([null, null]);
    const [page, setPage] = useState(1);
    const [scopeOptions, setScopeOptions] = useState([]);

    const params = useMemo(() => {
        const query = new URLSearchParams({ contingency, type, range, page: String(page) });

        if (range === RANGE_CUSTOM) {
            if (dates[0]) {
                query.set('from', dates[0]);
            }
            if (dates[1]) {
                query.set('to', dates[1]);
            }
        }

        return query;
    }, [contingency, type, range, dates, page]);

    const fetchMovements = useCallback(() => {
        setLoading(true);

        return axios
            .get(`${props.movementsUrl}?${params.toString()}`)
            .then((res) => {
                setRows(res.data.rows);
                setMeta(res.data.meta);
                setClosingBalance(res.data.closing_balance);

                if (range === RANGE_BUDGET && contingency === 'all' && type === 'all' && typeof props.onCountChange === 'function') {
                    props.onCountChange(res.data.meta.total);
                }
            })
            .finally(() => setLoading(false));
    }, [props.movementsUrl, params, range, contingency, type, props.onCountChange]);

    useEffect(() => {
        fetchMovements();
    }, [fetchMovements]);

    // The Contingency filter lists exactly the scopes that hold a balance, read from
    // the same payload the other tab renders.
    useEffect(() => {
        axios
            .get(props.rulesUrl)
            .then((res) => {
                setScopeOptions(
                    (res.data.rows || [])
                        .filter((row) => row.supports_contingency)
                        .map((row) => ({ value: `${row.scope.type}:${row.scope.id}`, label: row.name })),
                );
            })
            .catch(() => setScopeOptions([]));
    }, [props.rulesUrl]);

    const resetToFirstPage = (setter) => (value) => {
        setPage(1);
        setter(value);
    };

    // Position in the whole ledger, not in the page that happens to be loaded.
    const rangeFrom = meta.total === 0 ? 0 : (meta.current_page - 1) * meta.per_page + 1;
    const rangeTo = Math.min(meta.current_page * meta.per_page, meta.total);

    return (
        <div className="table__card tw-flex tw-flex-col tw-gap-6">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2.5">
                <label className="bc-filter tw-mb-0">
                    {$t('Contingency')}:
                    <select
                        className="bc-filter__select"
                        value={contingency}
                        aria-label={$t('Contingency')}
                        onChange={(e) => resetToFirstPage(setContingency)(e.target.value)}
                    >
                        <option value="all">{$t('All contingencies')}</option>
                        {scopeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="bc-filter tw-mb-0">
                    {$t('Type')}:
                    <select className="bc-filter__select" value={type} aria-label={$t('Type')} onChange={(e) => resetToFirstPage(setType)(e.target.value)}>
                        <option value="all">{$t('All types')}</option>
                        <option value="Manual">{$t('Manual')}</option>
                        {/* The value stays the stored log_type the server filters on; only
                            the label follows the screen's "Budget change" wording. */}
                        <option value="Budget Change">{$t('Budget change')}</option>
                    </select>
                </label>

                <label className="bc-filter tw-mb-0">
                    {$t('Date range')}:
                    <select
                        className="bc-filter__select"
                        value={range}
                        aria-label={$t('Date range')}
                        onChange={(e) => resetToFirstPage(setRange)(e.target.value)}
                    >
                        <option value={RANGE_BUDGET}>{$t('This budget version')}</option>
                        <option value={RANGE_ALL}>{$t('All time')}</option>
                        <option value={RANGE_CUSTOM}>{$t('Custom range')}</option>
                    </select>
                </label>

                {range === RANGE_CUSTOM && (
                    <div className="[&_.react-datepicker-popper]:tw-z-20">
                        <DateRangePickerField
                            id="movementsDateRange"
                            startDate={dates[0]}
                            endDate={dates[1]}
                            valueFormat="yyyy-MM-dd"
                            dateFormat="yyyy-MM-dd"
                            placeholderText={$t('Select dates')}
                            className="bc-input tw-text-start"
                            onChange={(value) => resetToFirstPage(setDates)(value)}
                        />
                    </div>
                )}

                <div className="tw-grow" />

                <Button variant="outline" small as="a" href={`${props.exportUrl}?${params.toString()}`} data-action="navigate">
                    <FileExportIcon className="tw-w-3 tw-h-3" />
                    {$t('Export CSV')}
                </Button>
            </div>

            <div className="tw-flex tw-items-center tw-space-x-3">
                <PaginationInfo from={rangeFrom} to={rangeTo} total={meta.total} />
                <TablePagination lastPage={meta.last_page} currentPage={meta.current_page} pageChange={(data) => setPage(data.selected + 1)} />
            </div>

            {loading ? <LoadingSpinner /> : <MovementsTable rows={rows} closingBalance={closingBalance} showContingencyColumn={closingBalance === undefined} />}

            <div className="bc-hint">
                <span className="bc-hint__icon" aria-hidden="true">
                    &#9432;
                </span>
                {$t(
                    'Additions to contingency show as positive; draw-downs show in brackets. In the All view, the Balance column shows the balance of the contingency each movement belongs to, with a Contingency column added.',
                )}
            </div>
        </div>
    );
}
